import { Chip } from "$components/chip";
import { gql } from "@apollo/client";
import { Box } from "@material-ui/core";
import { isValid, parseISO, subMonths } from "date-fns";
import { format } from "date-fns-tz";
import { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useSearchParams } from "react-router-dom";
import { AncEncounterStatus } from "../../../__generated__/base.gql";
import { useElephant200AdditionsFlag } from "../../patient/[:patientId]/antenatal-care/_lib/use-elephant-2-00-additions-flag";
import { DATE_FORMAT, PAGE_SIZE } from "../_lib/constants";
import { RegisterTableHeader } from "../_lib/register-table-header";
import {
  useAncAwaitingFinalisationCountQuery,
  useAncRegisterLazyQuery,
} from "./__generated__/page.gql";
import { AncRegisterTable } from "./_lib/anc-register-table";

gql`
  query AncAwaitingFinalisationCount($input: QueryAncEncountersInput!) {
    ancEncounters(input: $input) {
      total
    }
  }
`;

gql`
  query AncRegister($input: QueryAncEncountersInput!) {
    ancEncounters(input: $input) {
      skip
      take
      total
      encounters {
        id
        createdAt
        status
        dueDate
        parityDeliveries
        parityMiscarriages
        lastMenstrualPeriod
        ageOfPregnancy
        visitNumber
        iptpDoseOverride

        # counselling
        earlyInitiationOfBreastfeeding
        exclusiveBreastfeeding
        femaleGenitalMutilation
        hivTesting
        maternalNutrition
        longLastingInsecticidalNet

        investigations {
          _id
          catalogueItem {
            title
          }
          outcome {
            _id
          }
        }

        prescriptions {
          id
          clinicalProduct {
            ... on Vmp {
              title
              id
            }
            ... on Amp {
              title
              id
            }
          }
        }

        patient {
          uuid
          cardId
          passport {
            uid
          }
          person {
            name {
              fullname
            }
            dob
          }
        }
      }
    }
  }
`;

export const AncRegister: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const startDateParam = searchParams.get("startDate") ?? "";
  const endDateParam = searchParams.get("endDate") ?? "";
  const currentPageParam = Number(searchParams.get("currentPage"));
  const [currentPage, setCurrentPage] = useState(
    !Number.isNaN(currentPageParam) ? currentPageParam : 0,
  );
  const [startDate, setStartDate] = useState(
    isValid(parseISO(startDateParam))
      ? startDateParam
      : format(subMonths(new Date(), 1), DATE_FORMAT),
  );
  const [endDate, setEndDate] = useState(
    isValid(parseISO(endDateParam))
      ? endDateParam
      : format(new Date(), DATE_FORMAT),
  );

  const [fetchRecords, { data, loading }] = useAncRegisterLazyQuery();
  const elephant200AdditionsFlagOn = useElephant200AdditionsFlag();

  const {
    data: awaitingData,
    loading: awaitingLoading,
    error: awaitingError,
  } = useAncAwaitingFinalisationCountQuery({
    variables: {
      input: {
        params: {
          status: [AncEncounterStatus.InProgress],
        },
        options: {
          skip: 0,
          take: 1,
        },
      },
    },
    fetchPolicy: "cache-and-network",
    skip: !elephant200AdditionsFlagOn,
  });
  const awaitingFinalisationCount = awaitingData?.ancEncounters.total;

  useEffect(() => {
    if (startDate && endDate) {
      fetchRecords({
        variables: {
          input: {
            params: {
              startDate,
              endDate,
              // Off-flag: restore the pre-PNE-272 filter so only finalised
              // visits appear in the register.
              ...(elephant200AdditionsFlagOn
                ? {}
                : {
                    status: [
                      AncEncounterStatus.Completed,
                      AncEncounterStatus.Discharged,
                    ],
                  }),
            },
            options: {
              skip: currentPage * PAGE_SIZE,
              take: PAGE_SIZE,
            },
          },
        },
      });
    }
  }, [
    startDate,
    endDate,
    currentPage,
    fetchRecords,
    elephant200AdditionsFlagOn,
  ]);

  useEffect(() => {
    setSearchParams({ startDate, endDate, currentPage: `${currentPage}` });
  }, [startDate, endDate, currentPage, setSearchParams]);

  return (
    <>
      <RegisterTableHeader
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        setCurrentPage={setCurrentPage}
        heading={
          <FormattedMessage
            id="register.anc.title"
            defaultMessage="ANC Register"
          />
        }
        total={data?.ancEncounters.total ?? 0}
      />
      {elephant200AdditionsFlagOn && (
        <Box sx={{ mx: 2, mb: 2 }}>
          <Chip
            color="warning"
            data-testid="register.anc.awaitingFinalisation"
            label={
              awaitingError ? (
                <FormattedMessage
                  id="register.anc.awaitingFinalisation.error"
                  defaultMessage="Visits awaiting finalisation: unavailable"
                />
              ) : awaitingFinalisationCount === undefined && awaitingLoading ? (
                <FormattedMessage
                  id="register.anc.awaitingFinalisation.loading"
                  defaultMessage="Counting visits awaiting finalisation…"
                />
              ) : (
                <FormattedMessage
                  id="register.anc.awaitingFinalisation"
                  defaultMessage="Visits awaiting finalisation: {count, number}"
                  values={{ count: awaitingFinalisationCount ?? 0 }}
                />
              )
            }
          />
        </Box>
      )}

      <AncRegisterTable
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        loading={loading}
        data={data}
      />
    </>
  );
};
